import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  CheckCircle2, 
  Loader2, 
  CreditCard,
  AlertTriangle,
  ArrowLeft,
  Truck,
  Clock
} from "lucide-react";
import { publicApi } from "@/lib/api";

export default function ACIPaymentPage() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);  // Error loading invoice
  const [paymentError, setPaymentError] = useState(null);  // Error during payment
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Check if returning from payment
  const paymentStatus = searchParams.get('payment');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  // Check payment status if returning from Stripe
  useEffect(() => {
    if (paymentStatus === 'success' && sessionId) {
      checkPaymentStatus();
    }
  }, [paymentStatus, sessionId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      console.log('[ACI] Fetching invoice:', invoiceId);
      const response = await publicApi.get(`/aci/${invoiceId}`);
      console.log('[ACI] Invoice loaded:', response.data.invoice_number);
      setInvoice(response.data);
    } catch (err) {
      console.error('[ACI] Failed to load invoice:', err.response?.status, err.response?.data);
      setLoadError(err.response?.data?.detail || "Invoice not found");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      setCheckingPayment(true);
      const response = await publicApi.get(`/aci/${invoiceId}/payment-status?session_id=${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setInvoice(prev => ({
          ...prev,
          payment_status: 'paid',
          paid_at: response.data.paid_at
        }));
      }
    } catch (err) {
      console.error("Error checking payment status:", err);
    } finally {
      setCheckingPayment(false);
    }
  };

  const handlePayNow = async () => {
    try {
      setPaymentLoading(true);
      setPaymentError(null);
      console.log('[ACI PAY] Starting checkout for invoiceId:', invoiceId);
      const response = await publicApi.post(`/aci/${invoiceId}/checkout`);
      console.log('[ACI PAY] Checkout response:', response.data);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('[ACI PAY] Checkout error:', err.response?.status, err.response?.data);
      const errorDetail = err.response?.data?.detail || "Failed to create payment session";
      setPaymentError(errorDetail);
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#22c55e] animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (loadError && !invoice) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <div className="bg-[#0f172a] rounded-lg p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Invoice Not Found</h1>
          <p className="text-slate-400 mb-6">{loadError}</p>
          <Link to="/">
            <Button className="bg-[#22c55e] hover:bg-[#16a34a] text-black">
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isPaid = invoice?.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#22c55e] rounded-sm flex items-center justify-center">
              <Truck className="w-6 h-6 text-[#020617]" />
            </div>
            <div>
              <span className="font-['Barlow_Condensed'] text-lg font-bold text-white block leading-tight">
                RTS EXPRESS
              </span>
              <span className="text-[9px] text-slate-500">SAME DAY COURIER</span>
            </div>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Status Banner */}
        {isPaid ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-green-400 font-semibold">Payment Complete</p>
              <p className="text-green-400/70 text-sm">Thank you for your payment.</p>
            </div>
          </div>
        ) : checkingPayment ? (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin flex-shrink-0" />
            <p className="text-blue-400">Confirming your payment...</p>
          </div>
        ) : paymentStatus === 'success' && !isPaid ? (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <Clock className="w-6 h-6 text-yellow-500 flex-shrink-0" />
            <p className="text-yellow-400">Payment processing... Please wait a moment.</p>
          </div>
        ) : null}

        {/* Invoice Card */}
        <div className="bg-[#0f172a] rounded-lg border border-slate-800 overflow-hidden">
          {/* Invoice Header */}
          <div className="bg-[#1e293b] px-6 py-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-500 text-sm font-medium">Additional Charges Invoice</p>
                <p className="text-white text-2xl font-bold">{invoice?.invoice_number}</p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                isPaid 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-amber-500/20 text-amber-400'
              }`}>
                {isPaid ? 'PAID' : 'UNPAID'}
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-6 space-y-6">
            {/* Company Info */}
            {invoice?.company_name && (
              <div className="bg-[#020617] rounded-lg p-4">
                <p className="text-slate-400 text-sm mb-1">Billed To</p>
                <p className="text-white font-semibold">{invoice.company_name}</p>
                {invoice.contact_name && (
                  <p className="text-slate-400 text-sm">{invoice.contact_name}</p>
                )}
              </div>
            )}

            {/* Amount */}
            <div className="bg-[#020617] rounded-lg p-6 text-center">
              <p className="text-slate-400 text-sm mb-2">Amount Due</p>
              <p className="text-5xl font-bold text-amber-500">
                £{invoice?.total_amount?.toFixed(2)}
              </p>
              <p className="text-slate-500 text-sm mt-2">
                Including VAT (20%): £{invoice?.vat_amount?.toFixed(2)}
              </p>
            </div>

            {/* Reason */}
            <div className="bg-[#020617] rounded-lg p-4">
              <p className="text-amber-500 text-sm font-medium mb-2">Reason for Charge</p>
              <p className="text-white text-sm leading-relaxed">{invoice?.reason_text}</p>
              {invoice?.category && (
                <span className="inline-block mt-2 px-2 py-1 bg-slate-800 rounded text-xs text-slate-400">
                  {invoice.category.replace('_', ' ').toUpperCase()}
                </span>
              )}
            </div>

            {/* Reference */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-[#020617] rounded-lg p-4">
                <p className="text-slate-400 mb-1">Original Invoice</p>
                <p className="text-white font-medium">{invoice?.original_invoice_number || 'N/A'}</p>
              </div>
              <div className="bg-[#020617] rounded-lg p-4">
                <p className="text-slate-400 mb-1">Issue Date</p>
                <p className="text-white font-medium">
                  {invoice?.created_at ? new Date(invoice.created_at).toLocaleDateString('en-GB') : 'N/A'}
                </p>
              </div>
            </div>

            {/* Payment Button */}
            {!isPaid && (
              <div className="pt-4">
                {/* Payment Error Message */}
                {paymentError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-semibold">Payment Error</p>
                      <p className="text-red-400/70 text-sm">{paymentError}</p>
                      <p className="text-slate-500 text-xs mt-2">Please try again or contact support at quotes@rtsexpress.co.uk</p>
                    </div>
                  </div>
                )}
                
                <Button
                  onClick={handlePayNow}
                  disabled={paymentLoading}
                  className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold py-6 text-lg"
                  data-testid="aci-pay-now-btn"
                >
                  {paymentLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay Now – £{invoice?.total_amount?.toFixed(2)}
                    </>
                  )}
                </Button>
                <p className="text-center text-slate-500 text-xs mt-3">
                  Secure payment powered by Stripe
                </p>
              </div>
            )}

            {/* Paid Confirmation */}
            {isPaid && invoice?.paid_at && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-400 font-semibold">Payment Received</p>
                <p className="text-green-400/70 text-sm">
                  Paid on {new Date(invoice.paid_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-[#1e293b] px-6 py-4 border-t border-slate-700">
            <p className="text-slate-500 text-xs text-center">
              For any queries, please contact us at{' '}
              <a href="mailto:quotes@rtsexpress.co.uk" className="text-[#22c55e] hover:underline">
                quotes@rtsexpress.co.uk
              </a>
              {' '}quoting invoice number <span className="text-white">{invoice?.invoice_number}</span>
            </p>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <Link to="/" className="text-slate-500 hover:text-slate-300 text-sm inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Return to RTS Express
          </Link>
        </div>
      </main>
    </div>
  );
}
